import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const CartContext = createContext(null);

// Produkt-2für1 (z. B. Smash Cheese Mittwoch): je bezahltem Artikel 1 gratis (max 2),
// als eigene 0,00-€-Zeile — Menge immer synchron. Backend rechnet identisch (authoritativ).
function syncDealFreeLines(list) {
  const result = (list || []).filter(i => !i.deal_free);
  const groups = new Map();
  for (const i of result) {
    if (i.deal_type === '2for1' && i.deal_target_type === 'product' && !i.is_menu_upgrade &&
        (!i.deal_target_size || i.deal_target_size === (i.size || null))) {
      const g = groups.get(i.menu_item_id) || { qty: 0, src: i };
      g.qty += i.quantity;
      groups.set(i.menu_item_id, g);
    }
  }
  for (const [key, g] of groups) {
    const freeQty = Math.min(g.qty, 2);
    if (freeQty > 0) {
      result.push({
        menu_item_id: key,
        name: g.src.name,
        quantity: freeQty,
        size: g.src.size || null,
        size_label: g.src.size_label || null,
        is_menu_upgrade: false,
        modifiers: [], extras: [], removed_ingredients: [],
        total_price: 0,
        image_url: g.src.image_url,
        deal_free: true,
        deal_label: g.src.deal_label || 'Tagesangebot',
        cart_id: `free-${key}`,
      });
    }
  }
  return result;
}

export function CartProvider({ children }) {
  // Lazy Initializer: synchron aus localStorage laden — verhindert die Race-Condition,
  // bei der der Save-Effect den gespeicherten Warenkorb mit [] überschreibt.
  const [initial] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('cart') || '{}');
    } catch {
      return {};
    }
  });
  const [items, setItems] = useState(syncDealFreeLines(initial.items || []));
  const [discountCode, setDiscountCode] = useState(null);
  const [appliedDiscount, setAppliedDiscount] = useState(initial.appliedDiscount || null);
  const [lastAddedAt, setLastAddedAt] = useState(0);
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify({ items, appliedDiscount }));
  }, [items, appliedDiscount]);

  const addItem = useCallback((item) => {
    setLastAddedAt(Date.now());

    // Produkt-2für1 Tagesangebot: eigene GRATIS-Zeile wird automatisch synchron gehalten
    if (item.deal_type === '2for1' && item.deal_target_type === 'product' && !item.is_menu_upgrade &&
        (!item.deal_target_size || item.deal_target_size === (item.size || null))) {
      const already = itemsRef.current.some(i => i.deal_free && i.menu_item_id === item.menu_item_id);
      if (!already) {
        toast.success(`2-für-1 Deal aktiv! 1× ${item.name} GRATIS dazugelegt — Menge bleibt synchron. 🎉`, { duration: 5000 });
      }
    }

    setItems(prev => {
      // Check if same item exists (same ID, size, modifiers, extras)
      const existingIndex = prev.findIndex(i => 
        i.menu_item_id === item.menu_item_id &&
        i.size === item.size &&
        i.is_menu_upgrade === item.is_menu_upgrade &&
        JSON.stringify(i.modifiers) === JSON.stringify(item.modifiers) &&
        JSON.stringify(i.extras) === JSON.stringify(item.extras) &&
        JSON.stringify(i.removed_ingredients) === JSON.stringify(item.removed_ingredients)
      );

      if (existingIndex >= 0) {
        // Update quantity
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + item.quantity,
          total_price: updated[existingIndex].total_price + item.total_price
        };
        return syncDealFreeLines(updated);
      }

      // Add new item
      return syncDealFreeLines([...prev, { ...item, cart_id: Date.now().toString() }]);
    });
  }, []);

  const removeItem = useCallback((cartId) => {
    if (String(cartId).startsWith('free-')) return; // Gratis-Zeile wird automatisch verwaltet
    setItems(prev => syncDealFreeLines(prev.filter(item => item.cart_id !== cartId)));
  }, []);

  const updateQuantity = useCallback((cartId, quantity) => {
    if (String(cartId).startsWith('free-')) return; // Gratis-Zeile wird automatisch verwaltet
    if (quantity < 1) {
      removeItem(cartId);
      return;
    }

    setItems(prev => syncDealFreeLines(prev.map(item => {
      if (item.cart_id === cartId) {
        const unitTotal = item.total_price / item.quantity;
        return {
          ...item,
          quantity,
          total_price: unitTotal * quantity
        };
      }
      return item;
    })));
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
    setAppliedDiscount(null);
    setDiscountCode(null);
  }, []);

  const applyDiscount = useCallback((code, discount) => {
    setDiscountCode(code);
    setAppliedDiscount(discount);
  }, []);

  const removeDiscount = useCallback(() => {
    setDiscountCode(null);
    setAppliedDiscount(null);
  }, []);

  // Calculations
  const subtotal = items.reduce((sum, item) => sum + item.total_price, 0);
  const dealDiscount = (() => {
    let pctSum = 0;
    let fixedSum = 0;
    // Kategorie-2für1 (z. B. Pizza Dienstag): günstigere Einheit gratis (Basispreis ohne Extras).
    // Produkt-2für1 läuft über die eigene GRATIS-Zeile (0,00 €) und braucht keinen Abzug.
    const twoForOneGroups = new Map();
    for (const item of items) {
      if (item.deal_free) continue;
      if (item.deal_discount_pct) {
        pctSum += Math.round(item.total_price * (item.deal_discount_pct / 100) * 100) / 100;
      } else if (item.deal_fixed_amount) {
        const cap = Math.min(item.deal_fixed_amount * item.quantity, item.total_price);
        fixedSum += Math.round(cap * 100) / 100;
      } else if (item.deal_type === '2for1' && item.deal_target_type !== 'product') {
        const mods = (item.modifiers || []).reduce((s, m) => s + (m.price || 0), 0);
        const exts = (item.extras || []).reduce((s, e) => s + (e.price || 0) * (e.quantity || 1), 0);
        const baseUnit = Math.max(0, item.total_price / item.quantity - mods - exts);
        const key = item.deal_label || 'deal';
        const prev = twoForOneGroups.get(key) || { units: [] };
        for (let k = 0; k < item.quantity; k++) prev.units.push(baseUnit);
        twoForOneGroups.set(key, prev);
      }
    }
    let twoForOneSum = 0;
    for (const { units } of twoForOneGroups.values()) {
      if (units.length >= 2) {
        units.sort((a, b) => a - b);
        const freeCount = Math.min(Math.floor(units.length / 2), 2); // Backend: max_redemptions default 2
        for (let k = 0; k < freeCount; k++) twoForOneSum += units[k];
      }
    }
    return Math.round((pctSum + fixedSum + twoForOneSum) * 100) / 100;
  })();
  // Codes/Pickup-Rabatt gelten nur auf NICHT deal-rabattierte Artikel (Backend identisch)
  const dealedBase = items.reduce((s, i) => (i.deal_label && !i.deal_free && (i.deal_discount_pct || i.deal_fixed_amount || i.deal_type === '2for1')) ? s + i.total_price : s, 0);
  const eligibleBase = Math.max(0, subtotal - dealedBase);
  const discountAmount = appliedDiscount ?
    (appliedDiscount.type === 'percentage'
      ? eligibleBase * (appliedDiscount.value / 100)
      : Math.min(appliedDiscount.value, eligibleBase))
    : 0;
  const total = subtotal - discountAmount - dealDiscount;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      itemCount,
      subtotal,
      discountCode,
      appliedDiscount,
      discountAmount,
      dealDiscount,
      eligibleBase,
      total,
      lastAddedAt,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      applyDiscount,
      removeDiscount,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
